import { Box, Grid2, Stack, Typography } from '@mui/material'
import SuggestionAutocompleteField from 'components/fields/suggestion-autocomplete-field/suggestion-autocomplete-field.component'
import TextInputField from 'components/fields/text-input-field/text-input-field.component'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { AceSection, AceSelectField } from './ace-trip-fields'
import {
  AceSelectOption,
  AceTripFormValues,
  CANADIAN_PROVINCES,
  LICENSE_PLATE_STATES,
  PACKAGING_UNIT_OPTIONS,
  SHIPMENT_TYPE_OPTIONS,
  toAceWeightUnitInput,
  US_STATES,
} from './ace-trip-form'

type PartyFieldsProps = {
  title: string
  prefix: string
}

const PartyFields = ({ title, prefix }: PartyFieldsProps) => (
  <Box>
    <Typography fontWeight={700} sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    <Stack spacing={1.5}>
      <TextInputField name={`${prefix}.name`} label="Name *" />
      <TextInputField name={`${prefix}.address`} label="Address *" />
      <TextInputField name={`${prefix}.city`} label="City *" />
      <SuggestionAutocompleteField
        name={`${prefix}.stateProvince`}
        label="State / Province *"
        options={LICENSE_PLATE_STATES}
      />
      <TextInputField name={`${prefix}.postalCode`} label="Postal Code *" />
    </Stack>
  </Box>
)

type ShipmentCommodityProps = {
  index: number
  loadedOnOptions: AceSelectOption[]
}

const ShipmentCommodity = ({
  index,
  loadedOnOptions,
}: ShipmentCommodityProps) => (
  <Box sx={{ mt: 2.5 }}>
    <Typography fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
      Commodity
    </Typography>
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12, md: 3 }}>
        <TextInputField
          name={`shipments.${index}.commodities.0.description`}
          label="Description *"
          maxLength={450}
        />
      </Grid2>
      <Grid2 size={{ xs: 6, md: 1 }}>
        <TextInputField
          name={`shipments.${index}.commodities.0.quantity`}
          label="Quantity *"
        />
      </Grid2>
      <Grid2 size={{ xs: 6, md: 2 }}>
        <SuggestionAutocompleteField
          name={`shipments.${index}.commodities.0.packagingUnit`}
          label="Packaging Unit *"
          options={PACKAGING_UNIT_OPTIONS}
          transform={(value) => value.toUpperCase()}
          maxLength={3}
        />
      </Grid2>
      <Grid2 size={{ xs: 6, md: 2 }}>
        <TextInputField
          name={`shipments.${index}.commodities.0.weight`}
          label="Weight *"
        />
      </Grid2>
      <Grid2 size={{ xs: 6, md: 1 }}>
        <TextInputField
          name={`shipments.${index}.commodities.0.weightUnit`}
          label="Weight Unit *"
          transform={toAceWeightUnitInput}
          maxLength={3}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 3 }}>
        <AceSelectField
          name={`shipments.${index}.commodities.0.loadedOn`}
          label="Loaded On"
          options={loadedOnOptions}
        />
      </Grid2>
    </Grid2>
  </Box>
)

type ShipmentBlockProps = {
  index: number
  title: string
  manifestKind: AceTripFormValues['manifestKind']
  loadedOnOptions: AceSelectOption[]
}

const ShipmentBlock = ({
  index,
  title,
  manifestKind,
  loadedOnOptions,
}: ShipmentBlockProps) => {
  const loadingOptions = manifestKind === 'ACI' ? US_STATES : CANADIAN_PROVINCES

  return (
    <AceSection title={title}>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <AceSelectField
            name={`shipments.${index}.shipmentType`}
            label="Shipment Type *"
            options={SHIPMENT_TYPE_OPTIONS}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <TextInputField
            name={`shipments.${index}.shipmentControlNumber`}
            label="Shipment Control Number *"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <SuggestionAutocompleteField
            name={`shipments.${index}.provinceOfLoading`}
            label={
              manifestKind === 'ACI'
                ? 'State of Loading *'
                : 'Province of Loading *'
            }
            options={loadingOptions}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={3} sx={{ mt: 1 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <PartyFields title="Shipper" prefix={`shipments.${index}.shipper`} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <PartyFields
            title="Consignee"
            prefix={`shipments.${index}.consignee`}
          />
        </Grid2>
      </Grid2>

      <ShipmentCommodity index={index} loadedOnOptions={loadedOnOptions} />
    </AceSection>
  )
}

const AceTripShipmentsTab = () => {
  const { control } = useFormContext<AceTripFormValues>()
  const trailer = useWatch({ control, name: 'trailer' })
  const manifestKind = useWatch({ control, name: 'manifestKind' })
  const shipmentCount = useWatch({ control, name: 'shipments' })?.length ?? 0

  const loadedOnOptions = useMemo(() => {
    const trailerNumber = trailer?.trailerNumber?.trim()
    if (!trailerNumber) return []
    return [{ label: trailerNumber, value: trailerNumber }]
  }, [trailer])

  return (
    <Stack spacing={2.5}>
      {Array.from({ length: shipmentCount }, (_, index) => (
        <ShipmentBlock
          key={index}
          index={index}
          title={shipmentCount > 1 ? `Shipment ${index + 1}` : 'Shipment'}
          manifestKind={manifestKind}
          loadedOnOptions={loadedOnOptions}
        />
      ))}
    </Stack>
  )
}

export default AceTripShipmentsTab
